import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
// @ts-ignore
import { strapi as createStrapi } from '@strapi/client';

import { IHackathonProject, IHackathonQuery, IPaginationMeta, IVoteRanking } from '@/entities';
import { telegramClientApi } from '@/shared/api';
import { RootState } from '@/shared/store';
import { DEFAULT_PAGE_SIZE } from '@/constants';


export const client = createStrapi({
  baseURL: `${window.config.strapi.api_endpoint}/api`,
  auth: window.config.strapi.api_token || undefined
});

const COLLECTION_UID = 'hackathon-projects';

interface Pagination {
  page: number;
  pageSize: number;
  pageCount: number;
  total: number;
}

interface FetchListArg {
  query?: IHackathonQuery;
  append?: boolean;
}

export interface IHackathonProjectsState {
  items: IHackathonProject[];
  meta: IPaginationMeta | null;
  query: IHackathonQuery;
  loading: boolean;
  error: string | null;

  currentProject: IHackathonProject | null;
  currentProjectLoading: boolean;
  currentProjectError: string | null;

  voteRanking: IVoteRanking | null;
  voteRankingLoading: boolean;
  voteRankingError: string | null;
}

export interface HackathonProjectsState extends IHackathonProjectsState {
  loading: boolean;
  error: string | null;
}

const initialState: HackathonProjectsState = {
  items: [],
  meta: null,
  query: {
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE
  },

  currentProject: null,
  currentProjectLoading: false,
  currentProjectError: null,

  voteRanking: null,
  voteRankingLoading: false,
  voteRankingError: null,

  loading: false,
  error: null
};

export const fetchHackathonProjects = createAsyncThunk<
  { items: any[]; meta: Pagination | null; query: IHackathonQuery; append: boolean },
  FetchListArg | undefined,
  { state: RootState; rejectValue: string }
>(`hackathon/fetchList`, async (arg, { getState, rejectWithValue }) => {
  try {
    const root = getState();
    const slice = (root as any)?.hackathon as IHackathonProjectsState | undefined;

    const baseQuery = slice?.query ?? initialState.query;
    const qArg = arg?.query ?? {};
    const effectiveQuery: IHackathonQuery = { ...baseQuery, ...qArg };

    const page = qArg.page ?? baseQuery.page ?? 1;
    const pageSize = qArg.pageSize ?? baseQuery.pageSize ?? initialState.query.pageSize;

    const params: any = {
      pagination: { page, pageSize },
      populate: { project_logo: true }
    };

    if (effectiveQuery.sort) {
      params.sort = Array.isArray(effectiveQuery.sort)
        ? effectiveQuery.sort
        : [effectiveQuery.sort];
    }

    if (effectiveQuery.titleContains) {
      params.filters = { title: { $containsi: effectiveQuery.titleContains } };
    }

    const response = await client.collection(COLLECTION_UID).find(params);

    return {
      items: response.data || [],
      meta: (response.meta?.pagination as Pagination) || null,
      query: { ...effectiveQuery, page, pageSize },
      append: !!arg?.append
    };
  } catch (e: any) {
    return rejectWithValue(e?.message ?? 'Failed to fetch projects');
  }
});

export const fetchHackathonProject = createAsyncThunk<any, string, { rejectValue: string }>(
  `hackathon/fetchOne`,
  async (documentId, { rejectWithValue }) => {
    try {
      const response = await client.collection(COLLECTION_UID).findOne(documentId, {
        populate: { project_logo: true }
      });
      return response.data || null;
    } catch (e: any) {
      return rejectWithValue(e?.message ?? `Failed to fetch project: ${documentId}`);
    }
  }
);

export const refetchHackathonProjects = createAsyncThunk<any, void, { state: RootState }>(
  `hackathon/refetch`,
  async (_arg, { getState, dispatch }) => {
    const root = getState();
    const slice = (root as any)?.hackathon as IHackathonProjectsState | undefined;
    const query = slice?.query ?? initialState.query;
    return dispatch(fetchHackathonProjects({ query, append: false }));
  }
);

export const fetchVoteRanking = createAsyncThunk<IVoteRanking, void, { rejectValue: string }>(
  'hackathon/vote-ranking',
  async (_arg, { rejectWithValue }) => {
    try {
      const resp = await telegramClientApi.getVotesRanking();

      if (resp.error) {
        return rejectWithValue(resp.error.message ?? 'Failed to fetch votes ranking');
      }
      if (!resp.data) {
        return rejectWithValue('Failed to fetch votes ranking');
      }

      return resp.data;
    } catch (e: any) {
      return rejectWithValue(e?.message ?? 'Failed to fetch votes ranking');
    }
  }
);

const hackathonSlice = createSlice({
  name: 'hackathon',
  initialState,
  reducers: {
    setQuery: (state, action: PayloadAction<IHackathonQuery>) => {
      state.query = { ...state.query, ...action.payload };
    },
    resetQuery: (state) => {
      state.query = initialState.query;
    },
    clearCurrentProject: (state) => {
      state.currentProject = null;
      state.currentProjectError = null;
    },
    clearErrors: (state) => {
      state.error = null;
      state.currentProjectError = null;
      state.voteRankingError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchHackathonProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHackathonProjects.fulfilled, (state, action) => {
        state.loading = false;
        const append = action.payload.append === true;
        const nextItems = action.payload.items ?? [];
        state.items = append ? [...state.items, ...nextItems] : nextItems;
        state.meta = action.payload.meta;
        state.query = action.payload.query;
      })
      .addCase(fetchHackathonProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? 'Failed to fetch projects';
      });
    builder
      .addCase(fetchHackathonProject.pending, (state) => {
        state.currentProjectLoading = true;
        state.currentProjectError = null;
      })
      .addCase(fetchHackathonProject.fulfilled, (state, action) => {
        state.currentProjectLoading = false;
        state.currentProject = action.payload;
      })
      .addCase(fetchHackathonProject.rejected, (state, action) => {
        state.currentProjectLoading = false;
        state.currentProjectError = (action.payload as string) ?? 'Failed to fetch project';
      });
    builder
      .addCase(fetchVoteRanking.pending, (state) => {
        state.voteRankingLoading = true;
        state.voteRankingError = null;
      })
      .addCase(fetchVoteRanking.fulfilled, (state, action: PayloadAction<IVoteRanking>) => {
        state.voteRankingLoading = false;
        state.voteRanking = action.payload;
      })
      .addCase(fetchVoteRanking.rejected, (state, action) => {
        state.voteRankingLoading = false;
        state.voteRankingError = (action.payload as string) ?? 'Failed to fetch votes ranking';
      });
  }
});

export const { setQuery, resetQuery, clearCurrentProject, clearErrors } = hackathonSlice.actions;

export default hackathonSlice.reducer;
